<template>
  <teleport to="body">
    <transition name="fade">
      <div v-if="visible" class="image-lightbox" @click="handleClose">
        <!-- 关闭按钮 -->
        <button class="close-btn" @click.stop="handleClose">
          <el-icon><Close /></el-icon>
        </button>

        <!-- 下载按钮 -->
        <button class="download-btn" @click.stop="handleDownload">
          <el-icon><Download /></el-icon>
          <span>下载</span>
        </button>

        <!-- 图片名称 -->
        <div class="image-title">{{ imageName }}</div>

        <!-- 图片容器 -->
        <div class="image-container" @click.stop>
          <img
            v-if="imageUrl"
            :src="imageUrl"
            :alt="imageName"
            :style="imageStyle"
            @mousedown="startDrag"
            @mousemove="onDrag"
            @mouseup="endDrag"
            @mouseleave="endDrag"
            @wheel.prevent="handleWheel"
          >
        </div>

        <!-- 缩放控制 -->
        <div class="zoom-controls">
          <el-button circle @click="zoomOut">
            <el-icon><ZoomOut /></el-icon>
          </el-button>
          <span class="zoom-level">{{ Math.round(scale * 100) }}%</span>
          <el-button circle @click="zoomIn">
            <el-icon><ZoomIn /></el-icon>
          </el-button>
          <el-button circle @click="resetZoom">
            <el-icon><RefreshLeft /></el-icon>
          </el-button>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { Close, Download, ZoomOut, ZoomIn, RefreshLeft } from '@element-plus/icons-vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  imageUrl: {
    type: String,
    default: ''
  },
  imageName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'download'])

// 状态
const visible = ref(props.modelValue)
const scale = ref(1)
const translateX = ref(0)
const translateY = ref(0)
const isDragging = ref(false)
const dragStartX = ref(0)
const dragStartY = ref(0)
const dragStartTranslateX = ref(0)
const dragStartTranslateY = ref(0)

// 监听外部值变化
watch(() => props.modelValue, (val) => {
  visible.value = val
  if (val) {
    // 打开时重置缩放
    resetZoom()
  }
})

watch(() => visible.value, (val) => {
  emit('update:modelValue', val)
})

// 计算属性
const imageStyle = computed(() => ({
  transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`,
  cursor: isDragging.value ? 'grabbing' : 'grab'
}))

// 方法
const handleClose = () => {
  visible.value = false
}

const handleDownload = () => {
  emit('download', props.imageUrl)
}

const zoomIn = () => {
  scale.value = Math.min(scale.value * 1.2, 5)
}

const zoomOut = () => {
  scale.value = Math.max(scale.value / 1.2, 0.2)
}

const resetZoom = () => {
  scale.value = 1
  translateX.value = 0
  translateY.value = 0
}

const handleWheel = (e) => {
  if (e.deltaY < 0) {
    zoomIn()
  } else {
    zoomOut()
  }
}

const startDrag = (e) => {
  isDragging.value = true
  dragStartX.value = e.clientX
  dragStartY.value = e.clientY
  dragStartTranslateX.value = translateX.value
  dragStartTranslateY.value = translateY.value
}

const onDrag = (e) => {
  if (!isDragging.value) return
  const deltaX = e.clientX - dragStartX.value
  const deltaY = e.clientY - dragStartY.value
  translateX.value = dragStartTranslateX.value + deltaX
  translateY.value = dragStartTranslateY.value + deltaY
}

const endDrag = () => {
  isDragging.value = false
}
</script>

<style scoped lang="scss">
.image-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;

  .close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;
    z-index: 10;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }

    .el-icon {
      font-size: 24px;
    }
  }

  .download-btn {
    position: absolute;
    top: 20px;
    right: 76px;
    height: 44px;
    padding: 0 20px;
    border: none;
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s;
    z-index: 10;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }

    .el-icon {
      font-size: 18px;
    }

    span {
      font-size: 14px;
    }
  }

  .image-title {
    position: absolute;
    top: 20px;
    left: 20px;
    max-width: 50%;
    padding: 10px 16px;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    font-size: 14px;
    border-radius: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    z-index: 10;
  }

  .image-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
      max-width: 90%;
      max-height: 90%;
      object-fit: contain;
      user-select: none;
      transition: transform 0.1s ease-out;
    }
  }

  .zoom-controls {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 16px;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 24px;
    z-index: 10;

    .zoom-level {
      color: #fff;
      font-size: 14px;
      min-width: 50px;
      text-align: center;
    }
  }
}

// 过渡动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
